import { createMachine, assign, fromPromise } from 'xstate';
import { DeckStatus, StorySection, CardOffer, StoryInitializeResponse, PendingDiceRoll, AfterRollResponse, Card, UpgradeOffer, PlayActionResponse, SelectUpgradeResponse, InitiateStoryResponse } from '../types';
import { getTutorialGuidance } from '../tutorial/tutorialGuidance';
import axios from 'axios';
import { API_URL } from '../config';
import { fetchAfterRollStory, playCardAction, selectUpgrade, initiateStoryContinuation } from '../api/storyApi';
import { SavedSession, getSavedSessions, upsertSave, removeSave, getNextAdventureNumber, getOldestSave, isLocalStorageAvailable } from '../utils/sessionStorage';
import { hasAudioConsent } from '../utils/audioConsent';
import { log } from '../utils/logger';
import type { Variant } from '../utils/variant';

export interface GameFlowContext {
  // Core game state
  sessionId: string;
  deckStatus: DeckStatus | null;
  storySections: StorySection[];
  pendingStorySections: StorySection[];
  sectionQueue: StorySection[];
  currentQueueIndex: number;
  cardOffer: CardOffer | null;

  // UI state
  showCardOfferModal: boolean;
  showStoryContent: boolean;
  storyFadeIn: boolean;
  showContinueButton: boolean;
  showCards: boolean;
  savedScrollPosition: number | null;
  isInCardPlayMode: boolean;
  isFullScreenCardMode: boolean;

  // Tutorial bubble state
  tutorialBubble: {
    show: boolean;
    message: string;
    key?: string;
    position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    targetElement?: string;
    avatar?: string;
    buttonType?: 'continue' | 'dismiss' | 'none';
    requiresClick?: boolean;
    readinessStrategy?: 'simple' | 'animation' | 'boundingBoxStability' | 'staggeredList';
    delay?: number;
  } | null;

  // Game mode
  gameMode: 'tutorial' | 'normal';
  backendStep: number;
  typingComplete: boolean;

  // Survey modal state
  showSurveyModal: boolean;
  surveyOrigin: 'demo-end' | 'feedback-button' | null;
  demoEndAnswers: {
    wantToContinue: boolean;
    wantToGiveFeedback: boolean;
  } | null;

  // Dice flow state
  pendingDiceRoll: PendingDiceRoll | null;
  showDicePanel: boolean;
  diceAnimationComplete: boolean;
  afterRollData: AfterRollResponse | null;
  afterRollError: string | null;

  // Error state
  error: string | null;

  // Initialization
  existingSessionId: string | null;
  variant: Variant;

  // Landing page state
  showLandingPage: boolean;
  landingSavedSessions: SavedSession[];
  currentAdventureNumber: number;

  // Audio consent gate
  showAudioConsent: boolean;

  // Upgrade system state
  pendingUpgrade: UpgradeOffer | null;
  playedCard: Card | null;
  showUpgradeModal: boolean;
  pendingCardName: string;
  pendingActionPhrase: string;
  isAnimatingAsterisk: boolean;
  // True once /play-action + upgrade steps are done, i.e. the action can be
  // safely replayed from awaitingInitiateResponse without re-running them.
  canRetryAction: boolean;

  // Turn limit state
  turnsRemaining: number | null;
  turnLimit: number | null;
}

// Initialize service - fetches initial data from backend
const initializeService = fromPromise(async ({ input }: { input: { existingSessionId: string | null } }) => {
  log.debug('[initializeService] Starting with session:', input.existingSessionId);

  const response = await axios.post<StoryInitializeResponse>(`${API_URL}/story/initialize`, {
    session_id: input.existingSessionId || undefined
  });
  
  const newSessionId = response.data.session_id;
  log.debug('[initializeService] Backend returned sessionId:', newSessionId);
  
  // Update URL with session ID
  const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?session_id=${newSessionId}`;
  window.history.replaceState({ path: newUrl }, '', newUrl);
  
  // Check tutorial status
  let tutorialCompleted = response.data.tutorial_completed || false;
  let backendStep = response.data.tutorial_step || 0;
  
  if (tutorialCompleted) {
    log.debug("Detected completed tutorial from server response");
  }
  
  log.debug("Initialize service received tutorial_step:", backendStep, "tutorial_completed:", tutorialCompleted);
  
  return {
    sessionId: newSessionId,
    deckStatus: response.data.deck_status,
    storySections: response.data.story_sections,
    tutorialCompleted,
    backendStep,
    currentPuzzle: response.data.current_puzzle,
    turnsRemaining: response.data.turns_remaining,
    turnLimit: response.data.turn_limit
  };
});

// After-roll service - fetches branch sections after dice roll
const afterRollService = fromPromise(async ({ input }: { input: { sessionId: string; lastSectionId: number } }) => {
  log.debug('[afterRollService] Starting with sessionId:', input.sessionId, 'lastSectionId:', input.lastSectionId);

  const response = await fetchAfterRollStory(input.sessionId, input.lastSectionId);

  log.debug('[afterRollService] Received data:', response);

  return response;
});

// Play action service - calls /play-action for usage tracking and upgrade check
// Also waits for minimum animation time (400ms) so asterisk animation completes
const ASTERISK_ANIMATION_MS = 400;

const playActionService = fromPromise(async ({ input }: { input: { sessionId: string; cardName: string } }) => {
  log.debug('[playActionService] Starting with sessionId:', input.sessionId, 'cardName:', input.cardName);

  // Run API call and animation timer in parallel
  // Service won't complete until both are done
  const [response] = await Promise.all([
    playCardAction(input.sessionId, input.cardName),
    new Promise(resolve => setTimeout(resolve, ASTERISK_ANIMATION_MS))
  ]);

  log.debug('[playActionService] Received data:', response);

  return response;
});

// Select upgrade service - calls /card/select-upgrade to add upgrade card to deck
const selectUpgradeService = fromPromise(async ({ input }: { input: { sessionId: string; selectedCardName: string } }) => {
  log.debug('[selectUpgradeService] Starting with sessionId:', input.sessionId, 'selectedCardName:', input.selectedCardName);

  const response = await selectUpgrade(input.sessionId, input.selectedCardName);

  log.debug('[selectUpgradeService] Received data:', response);

  return response;
});

// Initiate story service - calls /continue-story/initiate for dice flow
const initiateStoryService = fromPromise(async ({ input }: { input: { sessionId: string; cardName: string; actionPhrase: string } }) => {
  log.debug('[initiateStoryService] Starting with sessionId:', input.sessionId, 'cardName:', input.cardName, 'actionPhrase:', input.actionPhrase);

  const response = await initiateStoryContinuation(input.sessionId, input.cardName, input.actionPhrase);

  log.debug('[initiateStoryService] Received data:', response);

  return response;
});

const filterBookCover = (sections: StorySection[]): StorySection[] =>
  sections.filter(s => !s.image?.url?.includes('book_cover.png'));

// Helper function to update tutorial bubble based on current game state
const updateTutorialBubble = (context: GameFlowContext, isShowingFirstParagraph?: boolean) => {
  log.debug('[updateTutorialBubble] Called with context:', {
    gameMode: context.gameMode,
    backendStep: context.backendStep,
    showStoryContent: context.showStoryContent,
    showCards: context.showCards,
    currentQueueIndex: context.currentQueueIndex,
    storySections: context.storySections.length,
    typingComplete: context.typingComplete,
    isShowingFirstParagraph
  });

  const guidance = getTutorialGuidance({
    gameMode: context.gameMode,
    storySections: context.storySections,
    showCards: context.showCards,
    showCardOfferModal: context.showCardOfferModal,
    showStoryContent: context.showStoryContent,
    backendStep: context.backendStep,
    tutorialCompleted: context.gameMode === 'normal',
    isInActionSelection: context.isInCardPlayMode,
    currentQueueIndex: context.currentQueueIndex,
    sectionQueue: context.sectionQueue,
    typingComplete: context.typingComplete,
    isShowingFirstParagraph,
    isFullScreenCardMode: context.isFullScreenCardMode
  });

  log.debug('[updateTutorialBubble] getTutorialGuidance returned:', guidance);

  return guidance ? {
    show: true,
    message: guidance.message,
    key: guidance.key,
    position: guidance.position || 'top',
    targetElement: guidance.targetElement,
    avatar: guidance.avatar || 'scientist',
    buttonType: guidance.buttonType || 'none',
    requiresClick: guidance.requiresClick || false,
    readinessStrategy: guidance.readinessStrategy || 'simple',
    delay: guidance.delay
  } : null;
};

export type GameFlowEvent =
  | { type: 'INITIALIZE'; sessionId: string; deckStatus: DeckStatus; storySections: StorySection[]; tutorialCompleted?: boolean; backendStep?: number }
  | { type: 'BACKEND_RESPONSE'; deckStatus: DeckStatus; storySections: StorySection[]; backendStep?: number; tutorialCompleted?: boolean }
  | { type: 'BACKEND_ERROR'; error: string }
  | { type: 'DISMISS_ERROR' }
  | { type: 'CARDS_ACCEPTED'; selectedCards: string[] }
  | { type: 'CARDS_SKIPPED' }
  | { type: 'STORY_DISPLAYED' }
  | { type: 'CARD_ACTION_SUBMITTED'; cardName: string; actionPhrase: string }
  | { type: 'TUTORIAL_COMPLETED' }
  | { type: 'CONTINUE_STORY' }
  | { type: 'TUTORIAL_CONTINUE' }
  | { type: 'TUTORIAL_DISMISS' }
  | { type: 'CARD_PLAY_MODE_CHANGED'; isInCardPlayMode: boolean }
  | { type: 'OPEN_SURVEY'; origin: 'demo-end' | 'feedback-button'; demoEndAnswers?: { wantToContinue: boolean; wantToGiveFeedback: boolean } }
  | { type: 'CLOSE_SURVEY' }
  | { type: 'LETS_ROLL_CLICKED' }
  | { type: 'DICE_ANIMATION_COMPLETE' }
  | { type: 'TYPING_COMPLETE' }
  | { type: 'EXIT_CARD_MODE' }
  | { type: 'ENTER_CARD_MODE' }
  | { type: 'UPGRADE_SELECTED'; selectedCardName: string }
  | { type: 'DECK_UPDATED'; deckStatus: DeckStatus }
  | { type: 'START_NEW_GAME' }
  | { type: 'RESUME_GAME'; sessionId: string }
  | { type: 'REPLACE_OLDEST_AND_START' }
  | { type: 'AUDIO_CONSENT_GIVEN' }
  | { type: 'RETRY_ACTION' };

export const gameFlowMachine = createMachine({
  id: 'gameFlow',
  initial: 'landingPage',
  context: ({ input }: { input?: { existingSessionId: string | null; variant?: Variant } }): GameFlowContext => ({
    sessionId: '',
    deckStatus: null,
    storySections: [],
    pendingStorySections: [],
    sectionQueue: [],
    currentQueueIndex: 0,
    cardOffer: null,
    showCardOfferModal: false,
    showStoryContent: false,
    storyFadeIn: false,
    showContinueButton: false,
    showCards: false,
    savedScrollPosition: null,
    isInCardPlayMode: false,
    isFullScreenCardMode: false,
    tutorialBubble: null,
    gameMode: 'tutorial' as const,
    backendStep: 0,
    typingComplete: false,
    showSurveyModal: false,
    surveyOrigin: null,
    demoEndAnswers: null,
    pendingDiceRoll: null,
    showDicePanel: false,
    diceAnimationComplete: false,
    afterRollData: null,
    afterRollError: null,
    error: null,
    existingSessionId: input?.existingSessionId || null,
    variant: input?.variant || 'launch',
    // Landing page state
    showLandingPage: false,
    landingSavedSessions: [],
    currentAdventureNumber: 1,
    // Audio consent gate
    showAudioConsent: false,
    // Upgrade system state
    pendingUpgrade: null,
    playedCard: null,
    showUpgradeModal: false,
    pendingCardName: '',
    pendingActionPhrase: '',
    isAnimatingAsterisk: false,
    canRetryAction: false,
    // Turn limit state
    turnsRemaining: null,
    turnLimit: null
  }),
  types: {} as {
    context: GameFlowContext;
    events: GameFlowEvent;
  },
  on: {
    // Tutorial bubble events
    TUTORIAL_CONTINUE: {
      actions: assign({
        tutorialBubble: ({ context }) => {
          if (context.tutorialBubble?.buttonType === 'continue') {
            return null; // Hide bubble after continue is clicked
          }
          return context.tutorialBubble;
        }
      })
    },
    TUTORIAL_DISMISS: {
      actions: assign({
        tutorialBubble: null
      })
    },
    // Card play mode change event
    CARD_PLAY_MODE_CHANGED: {
      actions: assign({
        isInCardPlayMode: ({ event }) => event.isInCardPlayMode,
        tutorialBubble: ({ context, event }) => updateTutorialBubble({
          ...context,
          isInCardPlayMode: event.isInCardPlayMode
        })
      })
    },
    // Typing complete event
    TYPING_COMPLETE: {
      actions: assign({
        typingComplete: true,
        tutorialBubble: ({ context }) => updateTutorialBubble({
          ...context,
          typingComplete: true
        })
      })
    },
    // Survey modal events
    OPEN_SURVEY: {
      actions: assign({
        showSurveyModal: true,
        surveyOrigin: ({ event }) => event.origin,
        demoEndAnswers: ({ event }) => event.demoEndAnswers || null
      })
    },
    CLOSE_SURVEY: {
      actions: assign({
        showSurveyModal: false,
        surveyOrigin: null,
        demoEndAnswers: null
      })
    },
    // Deck update event - can be sent from any state (e.g., paradox card plays)
    DECK_UPDATED: {
      actions: assign({
        deckStatus: ({ event }) => event.deckStatus
      })
    }
  },
  states: {
    landingPage: {
      entry: assign(({ context }) => {
        const storageAvailable = isLocalStorageAvailable();
        const saves = storageAvailable ? getSavedSessions() : [];

        // Variant C (/start): always start a fresh game, ignore saves and any URL session_id.
        // The first FTUE section becomes the landing experience.
        if (context.variant === 'start') {
          return {
            ...context,
            existingSessionId: null,
            showLandingPage: false,
            landingSavedSessions: saves,
            currentAdventureNumber: getNextAdventureNumber(),
          };
        }

        // If URL has a session_id, check if it's in saves
        if (context.existingSessionId) {
          const existingInSaves = saves.find(s => s.sessionId === context.existingSessionId);
          if (existingInSaves) {
            // Update lastPlayedAt and auto-resume
            upsertSave({ ...existingInSaves, lastPlayedAt: Date.now() });
            return {
              ...context,
              showLandingPage: false,
              landingSavedSessions: saves,
              currentAdventureNumber: existingInSaves.adventureNumber,
            };
          } else {
            // New session_id from URL — add to saves and auto-resume
            const adventureNumber = getNextAdventureNumber();
            upsertSave({
              sessionId: context.existingSessionId,
              adventureNumber,
              chapterNumber: 1,
              chapterTitle: 'Chapter 1',
              lastPlayedAt: Date.now(),
            });
            return {
              ...context,
              showLandingPage: false,
              landingSavedSessions: getSavedSessions(),
              currentAdventureNumber: adventureNumber,
            };
          }
        }

        // No URL session_id — show landing page
        return {
          ...context,
          showLandingPage: true,
          landingSavedSessions: saves,
        };
      }),
      always: [
        {
          // Auto-advance: URL had a session_id (resume) OR variant=start (skip landing)
          target: 'awaitingAudioConsent',
          guard: ({ context }) => !context.showLandingPage
        }
      ],
      on: {
        START_NEW_GAME: {
          target: 'awaitingAudioConsent',
          actions: assign({
            existingSessionId: null,
            showLandingPage: false,
            currentAdventureNumber: () => getNextAdventureNumber(),
          })
        },
        RESUME_GAME: {
          target: 'awaitingAudioConsent',
          actions: assign(({ event }) => {
            const saves = getSavedSessions();
            const save = saves.find(s => s.sessionId === event.sessionId);
            return {
              existingSessionId: event.sessionId,
              showLandingPage: false,
              currentAdventureNumber: save?.adventureNumber ?? 1,
            };
          })
        },
        REPLACE_OLDEST_AND_START: {
          target: 'awaitingAudioConsent',
          actions: assign(() => {
            const oldest = getOldestSave();
            if (oldest) removeSave(oldest.sessionId);
            return {
              existingSessionId: null,
              showLandingPage: false,
              currentAdventureNumber: getNextAdventureNumber(),
            };
          })
        }
      }
    },

    awaitingAudioConsent: {
      entry: assign({
        showAudioConsent: () => !hasAudioConsent(),
      }),
      always: [
        {
          target: 'initializing',
          guard: ({ context }) => !context.showAudioConsent
        }
      ],
      on: {
        AUDIO_CONSENT_GIVEN: {
          target: 'initializing',
          actions: assign({ showAudioConsent: false })
        }
      }
    },

    initializing: {
      invoke: {
        id: 'initializeService',
        src: initializeService,
        input: ({ context }) => ({
          existingSessionId: context.existingSessionId
        }),
        onDone: {
          target: 'preparingStoryQueue',
          actions: [assign({
            sessionId: ({ event }) => event.output.sessionId,
            deckStatus: ({ event }) => event.output.deckStatus,
            storySections: ({ event, context }) => {
              const sections = filterBookCover(event.output.storySections);
              const isNewTutorial = !context.existingSessionId && !event.output.tutorialCompleted && (event.output.backendStep ?? 0) <= 1;

              if (isNewTutorial) {
                return [];
              }

              if (context.existingSessionId && sections.length > 3) {
                return sections.slice(0, -3);
              }

              if (context.existingSessionId && sections.length > 0) {
                return [];
              }

              return sections;
            },
            pendingStorySections: ({ event, context }) => {
              const sections = filterBookCover(event.output.storySections);
              const isNewTutorial = !context.existingSessionId && !event.output.tutorialCompleted && (event.output.backendStep ?? 0) <= 1;

              if (isNewTutorial) {
                return sections;
              }

              if (context.existingSessionId && sections.length > 0) {
                return sections.slice(-3);
              }

              return [];
            },
            sectionQueue: ({ event, context }) => {
              const sections = filterBookCover(event.output.storySections);
              const isNewTutorial = !context.existingSessionId && !event.output.tutorialCompleted && (event.output.backendStep ?? 0) <= 1;

              if (isNewTutorial) {
                return sections;
              }

              if (context.existingSessionId && sections.length > 0) {
                return sections.slice(-3);
              }

              return [];
            },
            currentQueueIndex: 0,
            gameMode: ({ event }) => event.output.tutorialCompleted ? 'normal' : 'tutorial',
            backendStep: ({ event }) => event.output.backendStep ?? 0,
            turnsRemaining: ({ event }) => event.output.turnsRemaining ?? null,
            turnLimit: ({ event }) => event.output.turnLimit ?? null,
            cardOffer: ({ event }) => event.output.deckStatus.cards_offered || null,
            tutorialBubble: ({ context, event }) => {
              const sections = filterBookCover(event.output.storySections);
              const isNewTutorial = !context.existingSessionId && !event.output.tutorialCompleted && (event.output.backendStep ?? 0) <= 1;

              let computedStorySections: StorySection[];
              if (isNewTutorial) {
                computedStorySections = [];
              } else if (context.existingSessionId && sections.length > 3) {
                computedStorySections = sections.slice(0, -3);
              } else if (context.existingSessionId && sections.length > 0) {
                computedStorySections = [];
              } else {
                computedStorySections = sections;
              }

              const newContext = {
                ...context,
                gameMode: event.output.tutorialCompleted ? 'normal' : 'tutorial',
                backendStep: event.output.backendStep ?? 0,
                storySections: computedStorySections,
                showStoryContent: false,
                showCards: false,
                showCardOfferModal: false
              };
              return updateTutorialBubble(newContext as GameFlowContext);
            }
          }),
          // Auto-save to localStorage after successful initialization
          ({ context, event }) => {
            const chapterStart = event.output.storySections.find(
              (s: StorySection) => s.section_type === 'chapter_start' && !s.image?.url?.includes('book_cover.png')
            );
            upsertSave({
              sessionId: event.output.sessionId,
              adventureNumber: context.currentAdventureNumber,
              chapterNumber: chapterStart?.chapter_info?.number ?? 1,
              chapterTitle: chapterStart?.chapter_info?.title ?? 'Chapter 1',
              lastPlayedAt: Date.now(),
            });
          }]
        },
        onError: [
          {
            // Stale session (Redis flushed) → remove from saves, return to landing
            target: 'landingPage',
            guard: ({ context, event }) => {
              const is404 = (event.error as { response?: { status?: number } })?.response?.status === 404;
              return is404 && !!context.existingSessionId;
            },
            actions: assign(({ context }) => {
              if (context.existingSessionId) {
                removeSave(context.existingSessionId);
              }
              const saves = getSavedSessions();
              return {
                existingSessionId: null,
                showLandingPage: true,
                landingSavedSessions: saves,
                error: null,
              };
            })
          },
          {
            target: 'error',
            actions: assign({
              error: ({ event }) => `Failed to initialize: ${event.error}`
            })
          }
        ]
      }
    },
    
    playing: {
      entry: [
        () => log.debug('[STATE] Entering playing state'),
        assign({
          showStoryContent: false, // Hide story when in full-screen card mode
          storyFadeIn: false,
          showCards: true,
          showContinueButton: false,
          showDicePanel: false,
          isFullScreenCardMode: true, // Enable full-screen card mode
          tutorialBubble: ({ context }) => updateTutorialBubble({
            ...context,
            showStoryContent: false,
            showCards: true,
            showContinueButton: false,
            isFullScreenCardMode: true
          })
        })
      ],
      on: {
        EXIT_CARD_MODE: {
          target: 'viewingStoryFromCardMode'
        },
        CARD_ACTION_SUBMITTED: [
          {
            // Tutorial step 4: Use upgrade flow then dice flow
            target: 'animatingAsterisk',
            guard: ({ context }) => context.gameMode === 'tutorial' && context.backendStep === 4,
            actions: assign({
              typingComplete: false,
              pendingCardName: ({ event }) => event.cardName,
              pendingActionPhrase: ({ event }) => event.actionPhrase,
              canRetryAction: false
            })
          },
          {
            // Normal (post-tutorial) gameplay: Use upgrade flow then dice flow
            target: 'animatingAsterisk',
            guard: ({ context }) => context.gameMode === 'normal',
            actions: assign({
              pendingCardName: ({ event }) => event.cardName,
              pendingActionPhrase: ({ event }) => event.actionPhrase,
              canRetryAction: false
            })
          },
          {
            // Other tutorial steps: Use non-dice flow (no upgrade system)
            target: 'awaitingResponse',
            guard: ({ context }) => context.gameMode === 'tutorial'
          }
        ],
        TUTORIAL_COMPLETED: {
          actions: assign({ gameMode: 'normal' })
        }
      }
    },
    
    awaitingResponse: {
      entry: assign({
        showStoryContent: true,
        storyFadeIn: false
      }),
      on: {
        BACKEND_RESPONSE: {
          target: 'processingResponse',
          actions: assign({
            deckStatus: ({ event }) => event.deckStatus,
            pendingStorySections: ({ event }) => event.storySections,
            cardOffer: ({ event }) => event.deckStatus.cards_offered || null,
            backendStep: ({ event, context }) => event.backendStep ?? context.backendStep,
            gameMode: ({ event, context }) => event.tutorialCompleted ? 'normal' : context.gameMode,
            error: null
          })
        },
        BACKEND_ERROR: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error
          })
        }
      }
    },

    // Upgrade Flow States
    // Animation state - keeps modal open while asterisk fills
    animatingAsterisk: {
      entry: [
        () => log.debug('[STATE] Entering animatingAsterisk'),
        assign({
          isAnimatingAsterisk: true
          // Keep modal open - don't change showCards or isFullScreenCardMode
        })
      ],
      invoke: {
        id: 'playActionService',
        src: playActionService,
        input: ({ context }) => ({
          sessionId: context.sessionId,
          cardName: context.pendingCardName
        }),
        onDone: [
          {
            // If upgrade offer received, show upgrade modal
            target: 'offeringUpgrade',
            guard: ({ event }) => !!event.output.upgrade_offer,
            actions: assign({
              playedCard: ({ event }) => event.output.played_card,
              pendingUpgrade: ({ event }) => event.output.upgrade_offer,
              showUpgradeModal: true,
              isAnimatingAsterisk: false,
              showCards: false,
              showStoryContent: false,
              isFullScreenCardMode: false
            })
          },
          {
            // No upgrade, continue to story
            target: 'awaitingInitiateResponse',
            actions: assign({
              playedCard: ({ event }) => event.output.played_card,
              pendingUpgrade: null,
              isAnimatingAsterisk: false,
              showCards: false,
              showStoryContent: false,
              isFullScreenCardMode: false
            })
          }
        ],
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => `Failed to play card: ${(event.error as Error).message}`,
            isAnimatingAsterisk: false
          })
        }
      }
    },

    offeringUpgrade: {
      entry: [
        () => log.debug('[STATE] Entering offeringUpgrade'),
        assign({
          showUpgradeModal: true
        })
      ],
      on: {
        UPGRADE_SELECTED: {
          target: 'callingSelectUpgrade',
          actions: assign({
            pendingCardName: ({ event }) => event.selectedCardName
          })
        }
      }
    },

    callingSelectUpgrade: {
      entry: [
        () => log.debug('[STATE] Entering callingSelectUpgrade')
      ],
      invoke: {
        id: 'selectUpgradeService',
        src: selectUpgradeService,
        input: ({ context }) => ({
          sessionId: context.sessionId,
          selectedCardName: context.pendingCardName
        }),
        onDone: {
          target: 'awaitingInitiateResponse',
          actions: assign({
            deckStatus: ({ event }) => event.output.deck_status,
            showUpgradeModal: false,
            pendingUpgrade: null,
            // Restore the original card name for the story continuation
            pendingCardName: ({ context }) => context.playedCard?.name || context.pendingCardName
          })
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => `Failed to select upgrade: ${(event.error as Error).message}`,
            showUpgradeModal: false
          })
        }
      }
    },
    
    processingResponse: {
      always: {
        // Always go to story queue first, we'll check for card offers after
        target: 'preparingStoryQueue',
        actions: assign({
          sectionQueue: ({ context }) => context.pendingStorySections,
          currentQueueIndex: 0
        })
      }
    },

    // New Dice Flow States for Post-Tutorial Gameplay
    awaitingInitiateResponse: {
      entry: [
        () => log.debug('[STATE] Entering awaitingInitiateResponse'),
        assign({
          showCards: false,
          showStoryContent: false,  // Hide story to show loading screen
          storyFadeIn: false,
          isFullScreenCardMode: false,
          // Clear stale data from previous flow to prevent short-circuit
          afterRollData: null,
          afterRollError: null,
          pendingDiceRoll: null,
          // Past /play-action + upgrade: the rest of the action is safe to replay.
          canRetryAction: true
        })
      ],
      invoke: {
        id: 'initiateStoryService',
        src: initiateStoryService,
        input: ({ context }) => ({
          sessionId: context.sessionId,
          cardName: context.pendingCardName,
          actionPhrase: context.pendingActionPhrase
        }),
        onDone: {
          target: 'diceFlow',
          actions: assign({
            pendingDiceRoll: ({ event }) => {
              const response = event.output;
              return {
                firstSection: response.next_section,
                actionDifficulty: response.action_difficulty,
                diceRollResult: response.dice_roll_result,
                triggeredEffects: response.triggered_effects || []
              };
            },
            storySections: ({ context, event }) => {
              return [...context.storySections, event.output.next_section];
            },
            error: null
          })
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => `Failed to start story: ${(event.error as Error).message}`
          })
        }
      }
    },

    // Compound state for dice flow - keeps afterRollService alive through entire flow
    diceFlow: {
      invoke: {
        id: 'afterRollService',
        src: afterRollService,
        input: ({ context }) => ({
          sessionId: context.sessionId,
          lastSectionId: context.pendingDiceRoll!.firstSection.section_id
        }),
        onDone: {
          actions: assign({
            afterRollData: ({ event }) => event.output,
            afterRollError: null,
            turnsRemaining: ({ event }) => event.output.turns_remaining ?? null,
            turnLimit: ({ event }) => event.output.turn_limit ?? null
          })
        },
        onError: {
          target: '#gameFlow.error',
          actions: [
            ({ event, context }) => {
              console.error('[afterRollService] Error:', event.error);
              log.debug('[Rollback] Removing first section from story');
              log.debug('[Rollback] First section ID:', context.pendingDiceRoll?.firstSection.section_id);
            },
            assign({
              // Rollback: Remove the first section that was shown to the user
              storySections: ({ context }) => {
                if (context.pendingDiceRoll) {
                  const firstSectionId = context.pendingDiceRoll.firstSection.section_id;
                  const filteredSections = context.storySections.filter(s => s.section_id !== firstSectionId);
                  log.debug('[Rollback] Sections before:', context.storySections.length, 'after:', filteredSections.length);
                  return filteredSections;
                }
                return context.storySections;
              },
              // Set error message explaining what happened
              error: ({ event }) => {
                const errorMessage = (event.error as Error).message || 'Failed to fetch story continuation';
                return `${errorMessage}. The story has been rolled back. Please try a different action.`;
              },
              afterRollError: ({ event }) => (event.error as Error).message || 'Failed to fetch story continuation',
              afterRollData: null,
              // Clear pending dice roll after rollback
              pendingDiceRoll: null
            })
          ]
        }
      },
      initial: 'showingFirstParagraph',
      states: {
        showingFirstParagraph: {
          entry: [
            () => log.debug('[STATE] Entering diceFlow.showingFirstParagraph'),
            assign({
              showStoryContent: true,
              storyFadeIn: true,
              showCards: false,
              showContinueButton: true,
              tutorialBubble: ({ context }) => updateTutorialBubble(context, true)
            })
          ],
          on: {
            LETS_ROLL_CLICKED: 'rollingDice'
          }
        },

        rollingDice: {
          entry: [
            () => log.debug('[STATE] Entering diceFlow.rollingDice'),
            assign({
              showContinueButton: false,
              showDicePanel: true,
              diceAnimationComplete: false,
              tutorialBubble: null  // Hide tutorial bubble during dice roll
            })
          ],
          on: {
            DICE_ANIMATION_COMPLETE: {
              target: 'awaitingBranchSections',
              actions: assign({
                diceAnimationComplete: true
              })
            }
          }
        },

        awaitingBranchSections: {
          entry: [
            () => log.debug('[STATE] Entering diceFlow.awaitingBranchSections'),
            assign({
              showContinueButton: true
            })
          ],
          on: {
            CONTINUE_STORY: {
              target: '#gameFlow.processingBranchResponse',
              guard: ({ context }) => context.afterRollData !== null
            }
          }
        }
      }
    },

    processingBranchResponse: {
      entry: [
        () => log.debug('[STATE] Entering processingBranchResponse')
      ],
      always: {
        target: 'preparingStoryQueue',
        actions: [assign({
          deckStatus: ({ context }) => context.afterRollData?.deck_status || context.deckStatus,
          cardOffer: ({ context }) => context.afterRollData?.deck_status.cards_offered || null,
          sectionQueue: ({ context }) => context.afterRollData?.story_sections || [],
          currentQueueIndex: 0,
          showDicePanel: false,
          pendingDiceRoll: null,
          afterRollData: null,
          afterRollError: null,
          gameMode: ({ context }) => {
            if (context.gameMode === 'tutorial' && context.backendStep === 4) {
              const hasChapterStart = context.afterRollData?.story_sections?.some(
                section => section.section_type === 'chapter_start'
              );
              if (hasChapterStart) {
                log.debug('[STATE] Tutorial completed - transitioning to normal mode');
                return 'normal';
              }
            }
            return context.gameMode;
          }
        }),
        // Auto-save on chapter transitions
        ({ context }) => {
          const chapterStart = context.afterRollData?.story_sections?.find(
            (s: StorySection) => s.section_type === 'chapter_start'
          );
          if (chapterStart?.chapter_info) {
            upsertSave({
              sessionId: context.sessionId,
              adventureNumber: context.currentAdventureNumber,
              chapterNumber: chapterStart.chapter_info.number,
              chapterTitle: chapterStart.chapter_info.title,
              lastPlayedAt: Date.now(),
            });
          }
        }]
      }
    },

    offeringCards: {
      entry: [
        () => log.debug('[STATE] Entering offeringCards'),
        assign({
          showCardOfferModal: true,
          showStoryContent: false,
          savedScrollPosition: () => window.scrollY,
          tutorialBubble: ({ context }) => updateTutorialBubble({
            ...context,
            showCardOfferModal: true,
            showStoryContent: false
          })
        })
      ],
      on: {
        CARDS_ACCEPTED: {
          target: 'playing',
          actions: [
            () => log.debug('[STATE] CARDS_ACCEPTED -> playing'),
            assign({
              showCardOfferModal: false,
              cardOffer: null,
              showCards: true,
              showStoryContent: true
            }),
            ({ context }) => {
              // Restore scroll position after modal closes
              if (context.savedScrollPosition !== null) {
                setTimeout(() => {
                  window.scrollTo(0, context.savedScrollPosition!);
                }, 0);
              }
            },
            assign({
              savedScrollPosition: null
            })
          ]
        },
        CARDS_SKIPPED: {
          target: 'playing',
          actions: [
            () => log.debug('[STATE] CARDS_SKIPPED -> playing'),
            assign({
              showCardOfferModal: false,
              cardOffer: null,
              showCards: true,
              showStoryContent: true
            }),
            ({ context }) => {
              // Restore scroll position after modal closes
              if (context.savedScrollPosition !== null) {
                setTimeout(() => {
                  window.scrollTo(0, context.savedScrollPosition!);
                }, 0);
              }
            },
            assign({
              savedScrollPosition: null
            })
          ]
        }
      }
    },
    
    preparingStoryQueue: {
      always: [
        {
          target: 'displayingSectionWithContinue',
          guard: ({ context }) => context.sectionQueue.length > 0
        },
        {
          target: 'offeringCards',
          guard: ({ context }) => !!context.cardOffer && context.cardOffer.offered_cards.length > 0
        },
        {
          target: 'playing',
          actions: assign({
            showCards: true
          })
        }
      ]
    },

    displayingSectionWithContinue: {
      entry: assign({
        showStoryContent: true,
        storyFadeIn: ({ context }) => {
          // Only fade in (and thus trigger scroll) for the very first section from backend
          // Not for subsequent sections in the queue
          return context.currentQueueIndex === 0 && context.pendingStorySections.length > 0;
        },
        showContinueButton: true,
        showCards: false,
        isFullScreenCardMode: false,
        storySections: ({ context }) => {
          const currentSection = context.sectionQueue[context.currentQueueIndex];
          return [...context.storySections, currentSection];
        },
        tutorialBubble: ({ context }) => updateTutorialBubble(context)
      }),
      on: {
        CONTINUE_STORY: {
          target: 'preparingNextSection',
          actions: assign({
            currentQueueIndex: ({ context }) => context.currentQueueIndex + 1
          })
        }
      }
    },
    
    preparingNextSection: {
      always: [
        {
          target: 'displayingSectionWithContinue',
          guard: ({ context }) => context.currentQueueIndex < context.sectionQueue.length
        },
        {
          // After all sections displayed, check if we have cards to offer
          target: 'offeringCards',
          guard: ({ context }) => !!context.cardOffer && context.cardOffer.offered_cards.length > 0,
          actions: assign({
            sectionQueue: [],
            currentQueueIndex: 0,
            pendingStorySections: [],
            showContinueButton: false
          })
        },
        {
          target: 'playing',
          actions: assign({
            sectionQueue: [],
            currentQueueIndex: 0,
            pendingStorySections: [],
            showContinueButton: false,
            showCards: true
          })
        }
      ]
    },

    viewingStoryFromCardMode: {
      entry: [
        () => log.debug('[STATE] Entering viewingStoryFromCardMode'),
        assign({
          showStoryContent: true,
          storyFadeIn: false,
          showCards: false,
          showContinueButton: true,
          isFullScreenCardMode: false,
          tutorialBubble: null
        })
      ],
      on: {
        ENTER_CARD_MODE: {
          target: 'playing'
        },
        CONTINUE_STORY: {
          target: 'playing'
        }
      }
    },

    error: {
      entry: assign({
        showStoryContent: true,
        showCardOfferModal: false,
        storyFadeIn: true,
        showCards: false,
        showDicePanel: false,
        isFullScreenCardMode: false
      }),
      on: {
        DISMISS_ERROR: {
          target: 'playing',
          actions: assign({
            error: null,
            afterRollError: null,
            showCards: true
          })
        },
        // Replay the action from initiate -> roll -> after-roll. Gated on
        // canRetryAction so errors from /play-action or /select-upgrade (which
        // run before awaitingInitiateResponse) don't retry past those steps.
        // pendingCardName / pendingActionPhrase survive every error path.
        RETRY_ACTION: {
          target: 'awaitingInitiateResponse',
          guard: ({ context }) => context.canRetryAction,
          actions: assign({
            error: null,
            afterRollError: null
          })
        }
      }
    }
  }
});